/* eslint-disable clubmed/no-fake-component */
import React, { useState, useRef } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { sortBy, isEmpty } from 'lodash';
import moment from 'moment';

import { ICONS } from 'Components/utils/icon/icons';

import { Icon } from 'Components/frameworks/icon/Icon';
import { Label } from 'Components/frameworks/formik/formik-input/Label';

const DAY_MAX_LENGTH = 2;
const MONTH_MAX_LENGTH = 2;
const YEAR_MAX_LENGTH = 4;

const dayDefaultStruct = {
  refKey: 'day' as const,
  stateName: 'dayValue' as const,
  placeholderPropName: 'dayPlaceholder' as const,
  maxLength: DAY_MAX_LENGTH,
};
const monthDefaultStruct = {
  refKey: 'month' as const,
  stateName: 'monthValue' as const,
  placeholderPropName: 'monthPlaceholder' as const,
  maxLength: MONTH_MAX_LENGTH,
};
const yearDefaultStruct = {
  refKey: 'year' as const,
  stateName: 'yearValue' as const,
  placeholderPropName: 'yearPlaceholder' as const,
  maxLength: YEAR_MAX_LENGTH,
};

const datePatterns = [
  { key: 'd', struct: dayDefaultStruct },
  { key: 'm', struct: monthDefaultStruct },
  { key: 'y', struct: yearDefaultStruct },
];

type FieldValues = {
  dayValue: string;
  monthValue: string;
  yearValue: string;
};

type Props = {
  className?: string;
  dateFormat?: string;
  dayPlaceholder?: string;
  id?: string;
  isLoading?: boolean;
  isRequired?: boolean;
  label?: string;
  monthPlaceholder?: string;
  onBlur?: (date: string) => void;
  onChange?: (date: string) => void;
  validationState?: string;
  value?: string;
  yearPlaceholder?: string;
};

function computeInitialState(value: string | undefined, dateFormat: string): FieldValues {
  const formattedDate = moment(value, dateFormat);
  if (value && formattedDate.isValid()) {
    return {
      dayValue: formattedDate.format('DD'),
      monthValue: formattedDate.format('MM'),
      yearValue: formattedDate.format('YYYY'),
    };
  }
  return { dayValue: '', monthValue: '', yearValue: '' };
}

function areAllInputsFilled(values: FieldValues): boolean {
  return (
    values.dayValue.length === DAY_MAX_LENGTH &&
    values.monthValue.length === MONTH_MAX_LENGTH &&
    values.yearValue.length === YEAR_MAX_LENGTH
  );
}

function getFormattedDate(values: FieldValues, dateFormat: string): string {
  if (isEmpty(values.yearValue) && isEmpty(values.monthValue) && isEmpty(values.dayValue)) {
    return '';
  }
  return areAllInputsFilled(values)
    ? moment([+values.yearValue, +values.monthValue - 1, +values.dayValue]).format(dateFormat)
    : moment.invalid().toString();
}

/* DEPRECATED : DO NOT USE ANYMORE */
export function FormInputTelDate({
  className,
  dateFormat = 'DDMMYYYY',
  dayPlaceholder,
  id,
  isLoading,
  isRequired,
  label,
  monthPlaceholder,
  onBlur = () => {},
  onChange = () => {},
  validationState: validationStateProp,
  value,
  yearPlaceholder,
}: Props) {
  const [fieldValues, setFieldValues] = useState<FieldValues>(() =>
    computeInitialState(value, dateFormat),
  );

  const dayRef = useRef<HTMLInputElement>(null);
  const monthRef = useRef<HTMLInputElement>(null);
  const yearRef = useRef<HTMLInputElement>(null);

  const refs = { day: dayRef, month: monthRef, year: yearRef } as const;

  const placeholders = { dayPlaceholder, monthPlaceholder, yearPlaceholder } as const;

  function handleChange(
    fieldName: keyof FieldValues,
    event: React.ChangeEvent<HTMLInputElement>,
    nextElement: HTMLInputElement | null,
    maxLength: number,
  ) {
    const newValues = { ...fieldValues, [fieldName]: event.target.value };
    setFieldValues(newValues);
    onChange(getFormattedDate(newValues, dateFormat));

    if (nextElement && maxLength && event.target.value.length === maxLength) {
      nextElement.focus();
    }
  }

  function handleBlur(
    event: React.FocusEvent<HTMLInputElement>,
    nextRef: React.RefObject<HTMLInputElement> | null,
  ) {
    const relatedClassName = (event.relatedTarget as HTMLElement)?.className ?? '';
    if (!nextRef?.current || relatedClassName.indexOf('FormInputTelDate-input') === -1) {
      onBlur(getFormattedDate(fieldValues, dateFormat));
    }
  }

  function buildStructures() {
    const lowerFormat = dateFormat.toLowerCase();

    const patternsWithPos = datePatterns.map((p) => ({
      ...p,
      pos: lowerFormat.indexOf(p.key),
    }));

    const sorted = sortBy(patternsWithPos, ['pos']);

    const withSize = sorted.map((p) => ({
      ...p,
      size: (lowerFormat.match(new RegExp(p.key, 'g')) || []).length,
    }));

    return withSize.map(({ struct, size }, index, patterns) => {
      const nextRefKey = index + 1 < patterns.length ? patterns[index + 1].struct.refKey : null;
      const nextRef = nextRefKey ? refs[nextRefKey] : null;
      // Capture the current DOM element at render time for auto-focus.
      // On first render this is null (refs not committed yet), matching the
      // original class component behavior where this.refs was empty during render.
      const nextElement = nextRef?.current ?? null;

      return {
        refKey: struct.refKey,
        stateName: struct.stateName,
        inputRef: refs[struct.refKey],
        id: `${id}-${index}`,
        maxLength: size,
        onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
          handleChange(struct.stateName, event, nextElement, size),
        onBlur: (event: React.FocusEvent<HTMLInputElement>) => handleBlur(event, nextRef),
        placeholder: placeholders[struct.placeholderPropName],
      };
    });
  }

  const structures = buildStructures();

  const computedValidationState =
    validationStateProp === 'IS_VALID' && !areAllInputsFilled(fieldValues)
      ? 'NOT_VALIDATED'
      : validationStateProp;

  const isValid = computedValidationState === 'IS_VALID';
  const isInvalid = computedValidationState === 'IS_INVALID';

  return (
    <div className={classnames('flex flex-col', className)}>
      {!!label && (
        <Label className="mx-15 mb-5" htmlFor={id} isRequired={isRequired} label={label} />
      )}
      <div
        className={classnames(
          'outline-custom flex overflow-hidden rounded-pill border-1 transition-all duration-250 ease-in',
          {
            'border-success': isValid,
            'border-red': isInvalid,
          },
        )}
      >
        <div className="flex w-full items-center text-14">
          {structures.map((structure, index) => (
            <div className="relative flex flex-1 items-center" key={structure.refKey}>
              {!!index && <span>/</span>}
              <input
                className={classnames('h-42 w-full bg-transparent text-center', {
                  'cursor-progress': isLoading,
                })}
                maxLength={structure.maxLength}
                onBlur={structure.onBlur}
                onChange={structure.onChange}
                placeholder={structure.placeholder}
                ref={structure.inputRef}
                type="tel"
                value={fieldValues[structure.stateName]}
              />
            </div>
          ))}
        </div>
        {(isValid || isInvalid) && (
          <span
            className={classnames(
              'flex items-center justify-center px-10 text-10 transition-all duration-250 ease-in',
              {
                'text-success': isValid,
                'text-red': isInvalid,
              },
            )}
          >
            <Icon name={isValid ? ICONS.CHECK : ICONS.CROSS} width="0.8125rem" />
          </span>
        )}
      </div>
    </div>
  );
}
